import { toast } from "react-toastify";
import { setTitle } from "../../../../helpers/MetaTag";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";

import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { useState } from "react";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../../../components/form/Button";
import {
  reportDelete,
  reportList,
  reportOrdering,
  reportStatusChange,
} from "../../../../services/reportService";
import ActionBar from "../../../../components/elements/ActionBar";
import { lngDetect } from "../../../../helpers/lngDetect";
import Badge from "../../../../components/elements/Badge";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import ReportDetailsDrawer from "../../../../components/sections/admin/Report/Drawer/ReportDetailsDrawer";
import { eventList } from "../../../../services/eventService";
import Select from "../../../../components/form/select/Select";

const Report = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["report", filters, search],
    queryFn: async () => {
      const res = await reportList({
        ...filters,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const {
    data: eventData,
    isLoading: eventLoading,
    isFetching: eventFetching,
  } = useQuery({
    queryKey: ["Event"],
    queryFn: async () => {
      const res = await eventList({});
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  //   report status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        queryClient.setQueryData(["report", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || []).map((elm) =>
              elm?._id === id
                ? {
                    ...elm,
                    status: elm?.status === "active" ? "inactive" : "active",
                  }
                : elm
            );
            return { ...state, data: stateData };
          }
        });
        const res = await reportStatusChange({
          ids: id,
          status: status,
        });
        return { res, payload: { id, status } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          queryClient.setQueryData(["report", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = (stateData || []).map((elm) =>
                elm?._id === payload?.id
                  ? {
                      ...elm,
                      status: elm?.status === "active" ? "inactive" : "active",
                    }
                  : elm
              );
              return { ...state, data: stateData };
            }
          });
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const tableHeadData = [
    {
      _id: "name",
      name: "name",
      align: "left",
    },
    {
      _id: "email",
      name: "email",
      align: "left",
    },
    {
      _id: "reason",
      name: "reason",
      align: "left",
    },
    {
      _id: "Event",
      name: "event",
      align: "left",
      ...tableFilter({
        filterKey: "event",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val }));
        },
        filterList: eventData?.data?.map((item) => ({
          name: lngDetect(item?.title),
          value: item?._id,
          type: "checkbox",
        })),
      }),
    },
    {
      _id: 6,
      name: "Status",
      align: "left",
      ...tableFilter({
        filterKey: "status",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val }));
        },
        filterList: [
          { name: "pending", value: "pending", type: "checkbox" },
          { name: "active", value: "active", type: "checkbox" },
          { name: "invited", value: "invited", type: "checkbox" },
          { name: "deactivated", value: "deactivated", type: "checkbox" },
          { name: "deleted", value: "deleted", type: "checkbox" },
          { name: "inactive", value: "inactive", type: "checkbox" },
        ],
      }),
    },
    {
      _id: 8,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    console.log(item);
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.name || "-",
        },
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.email || "-",
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.reason || "-",
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: lngDetect(item?.eventData?.title) || "-",
        },
        {
          _id: 6,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <button
              type="button"
              onClick={() => {
                statusChangeHandler({
                  status: item?.status === "active" ? "inactive" : "active",
                  id: item?._id,
                });
              }}
            >
              <Badge
                label={item?.status}
                className={"capitalize"}
                type={item?.status === "active" ? "success" : "danger"}
              />
            </button>
          ),
        },
        {
          _id: "action",
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            // {
            //   _id: "edit",
            //   name: "Edit",
            //   icon: "fa-regular fa-pen-to-square",
            //   onClick: () => {
            //     setModalOpen({ isOpen: true, data: item });
            //   },
            //   standout: false,
            // },
            {
              _id: "details",
              name: "Details",
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
            {
              _id: "delete",
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  // add or edit page =================>
  function pageManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["report", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [...stateData, res?.data];
              return { ...state, data: stateData };
            }
          });
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(["report", filters, search]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(["report", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) =>
              item?._id === res?.data?._id ? { ...item, ...res?.data } : item
            );
            return { ...state, data: stateData };
          }
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["report", filters, search]);
        }
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   report delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await reportDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["report", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.filter(
              (item) => !payload?.includes(item?._id)
            );

            return { ...state, data: stateData };
          }
        });

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["report", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const options = [
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "User Delete",
    },
  ];

  function actionHandler(val) {
    switch (val) {
      case "userDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  const { mutate: handlereporting, isPending } = useMutation({
    mutationFn: async ({ newData, oldData }) => {
      const res = await reportOrdering({
        reporting_data: newData?.map((item, i) => ({
          id: item?._id,
          reporting: i,
        })),
      });
      return { res, newData, oldData };
    },
    onSuccess: ({ res, newData, oldData }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(["report", filters, search], (state) => {
          if (state) {
            return { ...state, data: oldData };
          }
        });
        toast.warning(res?.message || res?.error);
      }
    },
  });

  return (
    <div className="w-full flex flex-col pb-10 bg-white rounded-md">
      <div className="space-y-8 py-8 px-5">
        <div className="flex justify-between items-center border-b border-gray-300">
          <div className="flex flex-col gap-2 w-full">
            <div className="font-bold text-lg xl:text-2xl uppercase tracking-tighter font-Montserrat">
              Report Management
            </div>
            <div className="text-sm xl:text-base h-10">
              Easily handle all report actions: update report info, status, and
              add new report data.
            </div>
          </div>
          {/* <div className="relative">
            <Button
              buttonClasses={
                "!bg-transparent border border-transparent !h-10 hover:!bg-primary/10 hover:!border-primary/10"
              }
              variant="secondary"
              buttonLabel={"Create New"}
              buttonLabelClasses={
                "font-medium !text-sm uppercase whitespace-nowrap"
              }
              buttonHasLink
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div> */}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={data?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="w-72">
              <Search
                placeholder={"Search here"}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
            {selectedItems?.length > 0 && (
              <div className="flex items-center gap-2">
                <ActionBar options={options} onChange={actionHandler} />
                <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                  {selectedItems?.length} Selected
                  <button
                    onClick={() => setSelectedItems([])}
                    type="button"
                    className="cursor-pointer"
                  >
                    <i className="fa-fw fa-light fa-xmark"></i>
                  </button>
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Select
                selectedValue={"Buyer"}
                dropdownData={[{ name: "Buyer", value: "Buyer" }]}
                // onChange={(val) => {
                //   console.log({ val });
                //   handleChange({ target: { name: "parent", value: val } });
                // }}
                // onScroll={(e) => {
                //   loadData(e, categories, setCategoryilters);
                // }}
              />
            </div>
            <div className="relative">
              <Select
                selectedValue={"Past 3 month"}
                dropdownData={[{ name: "Past 3 month", value: "Past 3 month" }]}
                // onChange={(val) => {
                //   console.log({ val });
                //   handleChange({ target: { name: "parent", value: val } });
                // }}
                // onScroll={(e) => {
                //   loadData(e, categories, setCategoryilters);
                // }}
              />
            </div>
          </div>
        </div>
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          originalData={[...(data?.data || [])]}
          setOriginalData={(val) => {
            queryClient.setQueryData(["report", filters, search], (state) => {
              if (state) {
                return { ...state, data: val };
              }
            });
          }}
          handlereporting={(newData, oldData) =>
            handlereporting({ newData, oldData })
          }
          isreportingDisabled={false}
          isFetching={isFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
          selectable
        />
      </div>
      {/* <ManageReportDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={pageManageResponse}
      /> */}
      <ReportDetailsDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Report;
